function renderMarkdown(text) {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/```(\w*)\n?([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/### (.+)/g, '<h3>$1</h3>')
    .replace(/## (.+)/g, '<h2>$2</h2>')
    .replace(/# (.+)/g, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/^- (.+)/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
    .replace(/^(\d+)\. (.+)/gm, '<li>$2</li>')
    .replace(/(?:^|\\n)(<li>.*<\/li>\n?)+/g, '<ol>$&</ol>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');

  html = '<p>' + html + '</p>';
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<br><\/p>/g, '</p>');
  return html;
}

/* ── HEIC-to-JPEG converter ────────────────────────────────────────────
 *  Dynamically loads heic2any (WASM) from CDN only when a HEIC file is
 *  detected.  Returns the original file unchanged for non-HEIC images.
 */
async function convertHeicToJpegIfNeeded(file) {
  var isHeic = /\.heic$/i.test(file.name) ||
               /\.heif$/i.test(file.name) ||
               file.type === 'image/heic' ||
               file.type === 'image/heif';
  if (!isHeic) return file;

  // Dynamically load heic2any from CDN (only on first HEIC encounter)
  if (typeof heic2any === 'undefined') {
    await new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
      s.onload = resolve;
      s.onerror = function () { reject(new Error('Failed to load HEIC converter')); };
      document.head.appendChild(s);
    });
  }

  var result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
  // heic2any returns Blob | Blob[] — normalise to single blob
  var jpegBlob = Array.isArray(result) ? result[0] : result;
  var jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([jpegBlob], jpegName, { type: 'image/jpeg' });
}

class FloatingActions {
  constructor(fab) {
    this.fab = fab;
    this.apiUrl = fab.dataset.apiUrl || 'http://localhost:3001';
    this.waNumber = fab.dataset.waNumber || '919324931014';

    this.drawer = document.getElementById('floating-drawer');
    if (!this.drawer) return;

    this.messages = [];
    this.isLoading = false;
    this.chatHistory = [];
    this.cartId = null;
    this.colorFile = null;
    this._colorInitialized = false;

    this.bind();
  }

  bind() {
    this.fab.addEventListener('click', () => this.open());

    this.drawer.querySelector('.floating-backdrop').addEventListener('click', () => this.close());
    this.drawer.querySelector('.floating-close').addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    this.drawer.querySelectorAll('[data-view]').forEach((btn) => {
      btn.addEventListener('click', () => this.switchView(btn.dataset.view));
    });

    this.drawer.querySelectorAll('[data-back]').forEach((btn) => {
      btn.addEventListener('click', () => this.switchView('menu'));
    });

    const chatSend = this.drawer.querySelector('.floating-chat-send');
    const chatInput = this.drawer.querySelector('.floating-chat-input');
    chatSend.addEventListener('click', () => this.sendChat());
    chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendChat();
      }
    });
    chatInput.addEventListener('input', () => {
      const hasText = chatInput.value.trim().length > 0;
      chatSend.style.display = hasText ? '' : 'none';
    });
    chatSend.style.display = 'none';

    const waInput = this.drawer.querySelector('.floating-wa-input');
    const waSend = this.drawer.querySelector('.floating-wa-send');
    waSend.addEventListener('click', () => this.sendWhatsApp());
    waInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.sendWhatsApp();
      }
    });
  }

  open() {
    this.drawer.classList.add('floating-drawer--open');
    document.body.style.overflow = 'hidden';
    this.fab.style.display = 'none';
  }

  close() {
    this.drawer.classList.remove('floating-drawer--open');
    document.body.style.overflow = '';
    this.fab.style.display = '';
  }

  switchView(view) {
    this.drawer.querySelectorAll('.floating-view').forEach((v) => {
      v.classList.remove('floating-view--active');
    });
    const target = this.drawer.querySelector(`.floating-view[data-view-id="${view}"]`);
    if (target) target.classList.add('floating-view--active');
    if (view === 'chat') this.scrollChat();
    if (view === 'color' && !this._colorInitialized) {
      this._colorInitialized = true;
      this.setupColorUpload();
      this.setupColorPicker();
    }
  }

  /* ───── Chat ───── */

  addMessage(role, content, products) {
    const msg = {
      id: 'msg_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
      role: role,
      content: content,
      products: products || [],
    };
    this.messages.push(msg);
    return msg;
  }

  renderMessages() {
    const container = this.drawer.querySelector('.floating-chat-messages');
    container.innerHTML = '';

    this.messages.forEach((msg) => {
      const entry = document.createElement('div');
      entry.className = 'floating-message-entry';
      entry.id = msg.id;

      const row = document.createElement('div');
      row.className = msg.role === 'user'
        ? 'floating-msg-row floating-msg-row--user'
        : 'floating-msg-row floating-msg-row--assistant';

      const bubble = document.createElement('div');
      bubble.className = msg.role === 'user'
        ? 'floating-bubble floating-bubble--user'
        : 'floating-bubble floating-bubble--assistant';

      if (msg.role === 'assistant') {
        bubble.innerHTML = renderMarkdown(msg.content);
        entry._isAssistant = true;
        entry._content = msg.content;
      } else {
        bubble.textContent = msg.content;
      }

      row.appendChild(bubble);
      entry.appendChild(row);

      if (msg.products && msg.products.length > 0) {
        const productWrap = this.createProductCards(msg.products);
        entry.appendChild(productWrap);
      }

      container.appendChild(entry);
    });

    if (this.isLoading) {
      const loadingEntry = document.createElement('div');
      loadingEntry.className = 'floating-message-entry';
      loadingEntry.id = 'floating-loading';

      const row = document.createElement('div');
      row.className = 'floating-msg-row floating-msg-row--assistant';

      const bubble = document.createElement('div');
      bubble.className = 'floating-bubble floating-bubble--assistant';
      bubble.innerHTML = '<span class="floating-typing-dot"></span><span class="floating-typing-dot"></span><span class="floating-typing-dot"></span>';

      row.appendChild(bubble);
      loadingEntry.appendChild(row);
      container.appendChild(loadingEntry);
    }

    this.scrollChat();
  }

  createProductCards(products) {
    const wrap = document.createElement('div');
    wrap.className = 'floating-products-row';

    const track = document.createElement('div');
    track.className = 'floating-products-track';

    products.slice(0, 6).forEach((p) => {
      const card = document.createElement('div');
      card.className = 'floating-product-card';

      const imgLink = document.createElement('a');
      imgLink.href = this.getProductUrl(p.handle);
      imgLink.target = '_blank';
      imgLink.rel = 'noopener';

      const imgDiv = document.createElement('div');
      imgDiv.className = 'floating-product-img';

      if (p.image) {
        const img = document.createElement('img');
        img.src = p.image;
        img.alt = p.imageAlt || p.title;
        img.loading = 'lazy';
        imgDiv.appendChild(img);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'floating-product-fallback';
        fallback.textContent = p.title.charAt(0);
        imgDiv.appendChild(fallback);
      }

      imgLink.appendChild(imgDiv);
      card.appendChild(imgLink);

      const info = document.createElement('div');
      info.className = 'floating-product-info';

      const title = document.createElement('p');
      title.className = 'floating-product-title';
      title.textContent = p.title;

      const price = document.createElement('p');
      price.className = 'floating-product-price';
      price.textContent = '\u20B9' + Number(p.price).toLocaleString('en-IN');

      info.appendChild(title);
      info.appendChild(price);

      card.appendChild(info);
      track.appendChild(card);
    });

    wrap.appendChild(track);
    return wrap;
  }

  getProductUrl(handle) {
    if (!handle) return '#';
    return '/products/' + handle;
  }

  async addToCart(product) {
    this.showVariantPicker(product);
  }

  async showVariantPicker(product) {
    this.closeVariantPicker();
    const handle = product.handle;
    if (!handle) return;

    let productData;
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Failed to load product');
      productData = await res.json();
    } catch {
      window.location.href = `/products/${handle}`;
      return;
    }

    const selected = {};

    productData.options.forEach((opt) => {
      selected[opt.name] = opt.values[0];
    });

    const overlay = document.createElement('div');
    overlay.className = 'floating-variant-overlay';

    const panel = document.createElement('div');
    panel.className = 'floating-variant-panel';

    panel.innerHTML = `
      <button type="button" class="floating-variant-close" aria-label="Close">&times;</button>
      <div class="floating-variant-scroll">
        <div class="floating-variant-image">
          <img src="${productData.images?.[0]?.src || product.image || ''}" alt="${productData.title}" loading="lazy">
        </div>
        <div class="floating-variant-info">
          <h3 class="floating-variant-title">${productData.title}</h3>
          <p class="floating-variant-price" id="floating-variant-price">₹${Number(productData.variants[0]?.price || 0).toLocaleString('en-IN')}</p>
          <div class="floating-variant-options" id="floating-variant-options"></div>
          <div class="floating-variant-qty-wrap">
            <button type="button" class="floating-variant-qty-btn" id="floating-qty-minus">−</button>
            <span class="floating-variant-qty" id="floating-qty-value">1</span>
            <button type="button" class="floating-variant-qty-btn" id="floating-qty-plus">+</button>
          </div>
          <button type="button" class="floating-variant-cart-btn" id="floating-variant-add">Add to Cart</button>
        </div>
      </div>
    `;

    overlay.appendChild(panel);
    this.drawer.appendChild(overlay);

    requestAnimationFrame(() => overlay.classList.add('floating-variant-overlay--open'));

    const optionsContainer = panel.querySelector('#floating-variant-options');
    const priceEl = panel.querySelector('#floating-variant-price');
    const qtyEl = panel.querySelector('#floating-qty-value');

    let quantity = 1;

    function matchVariant() {
      return productData.variants.find((v) =>
        productData.options.every((opt) => v[`option${productData.options.indexOf(opt) + 1}`] === selected[opt.name])
      );
    }

    function updatePrice() {
      const v = matchVariant();
      if (v) {
        priceEl.textContent = '₹' + Number(v.price).toLocaleString('en-IN');
      }
    }

    productData.options.forEach((opt) => {
      const optDiv = document.createElement('div');
      optDiv.className = 'floating-variant-option';

      const label = document.createElement('label');
      label.className = 'floating-variant-option-label';
      label.textContent = opt.name;
      optDiv.appendChild(label);

      const pills = document.createElement('div');
      pills.className = 'floating-variant-pills';

      opt.values.forEach((val) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'floating-variant-pill';
        pill.textContent = val;
        if (val === selected[opt.name]) pill.classList.add('floating-variant-pill--active');

        pill.addEventListener('click', () => {
          pills.querySelectorAll('.floating-variant-pill').forEach((p) => p.classList.remove('floating-variant-pill--active'));
          pill.classList.add('floating-variant-pill--active');
          selected[opt.name] = val;
          updatePrice();
        });

        pills.appendChild(pill);
      });

      optDiv.appendChild(pills);
      optionsContainer.appendChild(optDiv);
    });

    panel.querySelector('#floating-qty-minus').addEventListener('click', () => {
      if (quantity > 1) {
        quantity--;
        qtyEl.textContent = quantity;
      }
    });

    panel.querySelector('#floating-qty-plus').addEventListener('click', () => {
      quantity++;
      qtyEl.textContent = quantity;
    });

    panel.querySelector('#floating-variant-add').addEventListener('click', () => {
      const v = matchVariant();
      if (!v) return;

      const formData = new FormData();
      formData.append('id', v.id);
      formData.append('quantity', String(quantity));

      fetch('/cart/add.js', { method: 'POST', body: formData })
        .then((r) => {
          if (!r.ok) throw new Error();
          return r.json();
        })
        .then((cart) => {
          this.closeVariantPicker();
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: v.id },
          }));
        })
        .catch(() => {
          window.location.href = `/products/${handle}?variant=${v.id}`;
        });
    });

    panel.querySelector('.floating-variant-close').addEventListener('click', () => this.closeVariantPicker());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeVariantPicker();
    });
  }

  closeVariantPicker() {
    const overlay = this.drawer.querySelector('.floating-variant-overlay');
    if (overlay) {
      overlay.classList.remove('floating-variant-overlay--open');
      overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    }
  }

  async sendChat() {
    const input = this.drawer.querySelector('.floating-chat-input');
    const text = input.value.trim();
    if (!text || this.isLoading) return;

    input.value = '';
    const sendBtn = this.drawer.querySelector('.floating-chat-send');
    sendBtn.style.display = 'none';

    this.addMessage('user', text);
    this.isLoading = true;
    this.renderMessages();

    this.chatHistory.push({ role: 'user', parts: [text] });

    try {
      const res = await fetch(`${this.apiUrl}/api/ucp/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: this.chatHistory,
          cartId: this.cartId,
        }),
      });

      if (!res.ok) throw new Error('Request failed');

      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Request failed');

      this.cartId = json.data.cartId || this.cartId;
      this.chatHistory.push({ role: 'model', parts: [json.data.response] });

      const msg = this.addMessage('assistant', json.data.response, json.data.products || []);
      this.isLoading = false;
      this.renderMessages();
      this.streamMessage(msg.id, json.data.response);

    } catch (err) {
      this.isLoading = false;
      this.addMessage('assistant', 'Sorry, I couldn\'t process that. Please try again.');
      this.renderMessages();
    }
  }

  streamMessage(msgId, fullContent) {
    const container = this.drawer.querySelector('.floating-chat-messages');
    const entry = container.querySelector('#' + CSS.escape(msgId));
    if (!entry) return;

    const bubble = entry.querySelector('.floating-bubble--assistant');
    if (!bubble) return;

    const words = fullContent.split(/(\s+)/);
    let idx = 0;
    let displayed = '';

    const cursor = document.createElement('span');
    cursor.className = 'floating-stream-cursor';
    bubble.appendChild(cursor);

    const interval = setInterval(() => {
      idx += 2;
      const chunk = words.slice(0, idx).join('');
      displayed = chunk;

      bubble.innerHTML = renderMarkdown(chunk);
      bubble.appendChild(cursor);

      if (idx >= words.length) {
        clearInterval(interval);
        bubble.innerHTML = renderMarkdown(fullContent);
        this.scrollChat();
      }
    }, 30);
  }

  scrollChat() {
    const container = this.drawer.querySelector('.floating-chat-messages');
    if (container) container.scrollTop = container.scrollHeight;
  }

  /* ───── Personal Color Profile ───── */

  setupColorUpload() {
    const body = this.drawer.querySelector('.flc-body');
    if (!body) return;

    const zone = body.querySelector('.flc-upload');
    if (!zone) return;

    var cameraInput = zone.querySelector('.flc-input--camera');
    var fileInput = zone.querySelector('.flc-input--file');

    // Click opens the PhotoPicker modal
    zone.addEventListener('click', function () {
      this.openColorPicker();
    }.bind(this));

    // Camera input (triggered by PhotoPicker "Take Photo")
    cameraInput.addEventListener('change', async (e) => {
      var file = e.target.files[0];
      if (file && (file.type.startsWith('image/') || /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name))) {
        this.colorFile = file;
        await this.analyzeColor();
      }
    });

    // File/gallery input (triggered by PhotoPicker "Choose from Gallery")
    fileInput.addEventListener('change', async (e) => {
      var file = e.target.files[0];
      if (file && (file.type.startsWith('image/') || /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name))) {
        this.colorFile = file;
        await this.analyzeColor();
      }
    });

    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      zone.style.borderColor = '#8c3a3f';
    });

    zone.addEventListener('dragleave', () => {
      zone.style.borderColor = '';
    });

    zone.addEventListener('drop', async (e) => {
      e.preventDefault();
      zone.style.borderColor = '';
      var file = e.dataTransfer.files[0];
      // Accept images by MIME type or .heic extension (iOS sometimes sends
      // HEIC as application/octet-stream in drag-and-drop).
      if (file && (file.type.startsWith('image/') || /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name))) {
        this.colorFile = file;
        await this.analyzeColor();
      }
    });
  }

  /* ─── Color PhotoPicker Modal ─── */

  get colorPicker() {
    return document.getElementById('flc-picker');
  }

  openColorPicker() {
    var picker = this.colorPicker;
    if (!picker) return;
    picker.setAttribute('aria-hidden', 'false');
    picker.classList.add('vto-picker--open');
    document.body.style.overflow = 'hidden';
  }

  closeColorPicker() {
    var picker = this.colorPicker;
    if (!picker) return;
    picker.setAttribute('aria-hidden', 'true');
    picker.classList.remove('vto-picker--open');
    document.body.style.overflow = '';
  }

  setupColorPicker() {
    var picker = this.colorPicker;
    if (!picker) return;

    // Close on backdrop click or Cancel
    picker.querySelectorAll('[data-flc-picker-close]').forEach(function (el) {
      el.addEventListener('click', this.closeColorPicker.bind(this));
    }.bind(this));

    // "Take Photo" — triggers camera input
    picker.querySelector('[data-flc-picker-camera]').addEventListener('click', function () {
      this.closeColorPicker();
      var zone = document.getElementById('flc-upload-zone');
      if (zone) {
        var cam = zone.querySelector('.flc-input--camera');
        if (cam) cam.click();
      }
    }.bind(this));

    // "Choose from Gallery" — triggers file input
    picker.querySelector('[data-flc-picker-gallery]').addEventListener('click', function () {
      this.closeColorPicker();
      var zone = document.getElementById('flc-upload-zone');
      if (zone) {
        var file = zone.querySelector('.flc-input--file');
        if (file) file.click();
      }
    }.bind(this));

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && this.colorPicker && this.colorPicker.classList.contains('vto-picker--open')) {
        this.closeColorPicker();
      }
    }.bind(this));
  }

  async analyzeColor() {
    if (!this.colorFile) return;

    // Convert HEIC to JPEG before preview/upload (browsers can't show HEIC previews)
    var converted = await convertHeicToJpegIfNeeded(this.colorFile);
    this.colorFile = converted; // store the JPEG for upload

    var previewUrl = URL.createObjectURL(converted);

    var body = this.drawer.querySelector('.flc-body');

    body.innerHTML = [
      '<div class="flc-loading">',
      '  <div class="flc-loading-img-wrap">',
      '    <img src="', previewUrl, '" alt="Uploaded" loading="lazy">',
      '    <div class="flc-loading-overlay">',
      '      <div class="flc-loading-spinner"></div>',
      '      <span class="flc-loading-text">Analyzing...</span>',
      '    </div>',
      '  </div>',
      '</div>'
    ].join('');

    try {
      const formData = new FormData();
      formData.append('image', this.colorFile);

      const res = await fetch(`${this.apiUrl}/api/analysis/skin-color`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Analysis failed');

      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Analysis failed');

      this.showColorResult(json.data, previewUrl);
    } catch (err) {
      body.innerHTML = `
        <div class="flc-error">
          <p>${err.message || 'Something went wrong'}</p>
          <button type="button" class="flc-error-btn" onclick="document.querySelector('.floating-drawer--open').__floating__.resetColorAnalysis()">Try Again</button>
        </div>
      `;
    }
  }

  showColorResult(data, previewUrl) {
    const body = this.drawer.querySelector('.flc-body');

    const bestHtml = data.bestColors && data.bestColors.length
      ? `<div class="flc-section">
          <h3 class="flc-section-title">Best Colors</h3>
          <div class="flc-pills">${data.bestColors.map(c => `<span class="flc-pill"><span class="flc-pill-dot" style="background:${c.hex}"></span>${c.name}</span>`).join('')}</div>
        </div>`
      : '';

    const avoidHtml = data.avoidColors && data.avoidColors.length
      ? `<div class="flc-section">
          <h3 class="flc-section-title">Colors to Avoid</h3>
          <div class="flc-pills">${data.avoidColors.map(c => `<span class="flc-pill flc-pill--avoid"><span class="flc-pill-dot" style="background:${c.hex}"></span>${c.name}</span>`).join('')}</div>
        </div>`
      : '';

    const descHtml = data.description
      ? `<p class="flc-description">${data.description}</p>`
      : '';

    body.innerHTML = `
      <div class="flc-result">
        ${previewUrl ? `<div class="flc-result-img"><img src="${previewUrl}" alt="Uploaded" loading="lazy"></div>` : ''}
        <div class="flc-result-card">
          <div class="flc-grid">
            <div class="flc-stat">
              <div class="flc-stat-label">Skin Tone</div>
              <div class="flc-stat-value">${data.skinTone}</div>
            </div>
            <div class="flc-stat">
              <div class="flc-stat-label">Undertone</div>
              <div class="flc-stat-value">${data.undertone}</div>
            </div>
            <div class="flc-stat">
              <div class="flc-stat-label">Season</div>
              <div class="flc-stat-value">${data.season}</div>
            </div>
          </div>
          ${bestHtml}
          ${avoidHtml}
          ${descHtml}
        </div>
        <div class="flc-actions">
          <button type="button" class="flc-btn-secondary" onclick="document.querySelector('.floating-drawer--open').__floating__.resetColorAnalysis()">Analyze Another</button>
        </div>
      </div>
    `;
  }

  resetColorAnalysis() {
    const body = this.drawer.querySelector('.flc-body');
    this.colorFile = null;
    body.innerHTML = `
      <div class="flc-upload" id="flc-upload-zone">
        <div class="flc-upload-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
          </svg>
        </div>
        <span class="flc-upload-text">Tap to add photo</span>
        <span class="flc-upload-hint">JPG, PNG, HEIC, or WebP</span>
        <input type="file" accept="image/*" capture="user" class="flc-input--camera" hidden>
        <input type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.HEIC,.HEIF" class="flc-input--file" hidden>
      </div>
      <div class="flc-card">
        <h3 class="flc-card-title">How it works</h3>
        <ol class="flc-steps">
          <li class="flc-step"><span class="flc-step-num">1</span>Upload a clear photo of your face</li>
          <li class="flc-step"><span class="flc-step-num">2</span>AI analyzes your skin tone &amp; undertone</li>
          <li class="flc-step"><span class="flc-step-num">3</span>Discover your seasonal color palette</li>
          <li class="flc-step"><span class="flc-step-num">4</span>Get personalized color recommendations</li>
        </ol>
        <p class="flc-disclaimer">AI analyzes your skin tone and recommend colors that complement your natural complexion.</p>
      </div>
    `;
    this.setupColorUpload();
  }

  /* ───── WhatsApp ───── */

  sendWhatsApp() {
    const input = this.drawer.querySelector('.floating-wa-input');
    const text = input.value.trim();
    if (!text) return;
    const url = `https://wa.me/${this.waNumber}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    input.value = '';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const fab = document.getElementById('floating-fab');
  if (fab) {
    const fa = new FloatingActions(fab);
    const drawer = document.getElementById('floating-drawer');
    if (drawer) drawer.__floating__ = fa;
  }
});
